import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Divider, Modal, Space } from 'antd';
import {
  useDeleteEmployee,
  useEmployeesReduced,
} from '@/core/domain/enterprise/Employees/Employees.hooks';
import {
  EmployeeReduced,
  ListEmployeesQuery,
} from '@/core/domain/enterprise/Employees/Employees.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { PageProps } from '@/types/PageProps';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import EmployeeReportModal from '@/components/Employee/EmployeeReportModal';
import { getTableScrollX } from '@/utils/table';
import { UserAvatar } from '@/components/_common';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EmployeeFiltersTable from '@/components/Employee/EmployeeFiltersTable';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { useDebounce } from '@/hooks';

const EmployeesPage = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState<ListEmployeesQuery>({
    order: 'ASC',
    isActive: true,
    ...convertParams<ListEmployeesQuery>({
      order: 'string',
      isActive: 'boolean',
      registry: 'string',
      function: 'string',
      search: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, fetchNextPage, hasNextPage } =
    useEmployeesReduced(queryDebounce);
  const employees = flatInfiniteData(data);

  const deleteMutation = useDeleteEmployee();

  const [modalReportVisible, setModalReportVisible] = useState(false);

  function toggleReportModal() {
    setModalReportVisible(!modalReportVisible);
  }

  const handleRemoveClick = (employee: EmployeeReduced) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'employee.remove.modal.title' }),
      content: `${intl.formatMessage({
        id: 'employee.remove.modal.content',
      })} "${employee.name}"`,
      onOk: async () => {
        try {
          // Fetch Service to remove group item.
          await deleteMutation.mutateAsync(employee.id);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'employee.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  const columns: ColumnsType<EmployeeReduced> = [
    {
      title: intl.formatMessage({ id: 'employee.list.header.name' }),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 300,
      render: (value, row) => {
        return (
          <Space>
            <UserAvatar gender={row.gender} picture={row.photo} />
            {value}
          </Space>
        );
      },
    },

    {
      title: intl.formatMessage({ id: 'employee.list.header.gender' }),
      dataIndex: 'gender',
      key: 'gender',
      width: 150,
    },
    {
      title: intl.formatMessage({ id: 'employee.list.header.registry' }),
      dataIndex: 'registry',
      key: 'registry',
      width: 150,
    },
    {
      title: intl.formatMessage({ id: 'employee.list.header.function' }),
      dataIndex: 'function',
      key: 'function',
      width: 150,
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      key: 'actions',
      width: 100,
      align: 'right',
      fixed: 'right',
      render: (row) => (
        <Space>
          <EditIcon
            redirect={`/app/registry/employees/${row.id}/personal`}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_VIEW])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(row)}
            disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Button
              block
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={
                !hasPermissions([Authorities.REGISTER_EMPLOYEE_REPORTS])
              }
            >
              <FormattedMessage id="suppliers.searchBar.generateReport" />
            </Button>

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/registry/employees/create')}
              disabled={!hasPermissions([Authorities.REGISTER_EMPLOYEE_ADD])}
            >
              <FormattedMessage id="employee.searchBar.button" />
            </Button>
          </SpaceForButton>
        }
      >
        <EmployeeFiltersTable
          queryParams={queryParams}
          setQueryParams={(values) => {
            setQueryParams(values);
          }}
        />

        <InfiniteTable<EmployeeReduced>
          loading={isFetching}
          columns={columns}
          dataSource={employees}
          rowKey={(row) => row.id}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />

        <EmployeeReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />
      </TabContentWrapper>
    </>
  );
};

export default EmployeesPage;
